import React, {
  useMemo, useEffect, useState, useCallback, ReactElement,
} from 'react';
import { useNavigate } from 'react-router';
import Table from '../../../../../../components/table/table.component';
import TableBody from '../../../../../../components/table-body/table-body.component';
import TableRow from '../../../../../../components/table-row/table-row.component';
import TableCell from '../../../../../../components/table-cell/table-cell.component';
import RequiredMark from '../../../../../../components/required-mark/required-mark.component';
import ChipLink from '../../../../../../components/chip-link/chip-link.component';
import FilteredLink from '../../../../../../components/filtered-link/filtered-link.component';
import Chip from '../../../../../../components/chip/chip.component';
import Hint from '../../../../../../components/hint/hint.component';
import FormFieldSelect from '../../../../../../components/form-field-select/form-field-select.component';
import FormFieldMultiSelect
  from '../../../../../../components/form-field-multi-select/form-field-multi-select.component';
import FormFieldText from '../../../../../../components/form-field-text/form-field-text.component';
import FormFieldPhone from '../../../../../../components/form-field-phone/form-field-phone.component';
import FormFieldNumber from '../../../../../../components/form-field-number/form-field-number.component';
import FormFieldCheckbox from '../../../../../../components/form-field-checkbox/form-field-checkbox.component';
import FormFieldAddress from '../../../../../../components/form-field-address/form-field-address.component';
import FieldAddressBlock from '../../../../../../components/field-address-block/field-address-block.component';
import FieldPhoneBlock from '../../../../../../components/field-phone-block/field-phone-block.component';
import ActionBlock from '../../../../../../components/action-block/action-block.component';
import MenuItemList from '../../../../../../components/menu-item-list/menu-item-list.component';
import SinglePageTab from '../../../../../app/common/single-page-tab/single-page-tab.component';
import FormatService from '../../../../../../services/format-service';
import FormService from '../../../../../../services/form-service';
import useScreenMobile from '../../../../../../hooks/use-screen-mobile';
import { CONTACT_VIEW } from '../../../../../../constants/entity/permissions';
import { ROUTE_CONTACT } from '../../../../../../constants/system/routes';
import CompanyDefaultImage from '../../../../../../static/images/company-default-image.png';

const SettingsUserSummary: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isSaving,
    isSaveDisabled,
    isEditMode,
    isRightDrawerOpen,
    isUserProfile,
    formError,
    userData,
    roleList,
    assetTypeList,
    contactCompanyList,
    onCancelForm,
    onSubmitForm,
    onPhoneChange,
    onPhoneWorkChange,
    onVerifyPhone,
    onAssetTypesChange,
    onContactCompanySearch,
    onContactCompanySearchClose,
    onAddressSearchCompleted,
    onAddressFieldKeyDown,
  } = props;

  const navigate: ReactRouterNavigate = useNavigate();
  const isMobileScreen: boolean = useScreenMobile();

  const userIsPhoneVerified: boolean = (!!userData && userData.isPhoneVerified) || false;

  const [selectedDeals, setSelectedDeals]: [Deal[] | null, Function] = useState(null);
  const [selectedPortfolios, setSelectedPortfolios]: [Portfolio[] | null, Function] = useState(null);
  const [accountUserType, setAccountUserType]: [AccountUserType | null, Function] = useState(null);
  const [userAddress, setUserAddress]: [JsonObject | null, Function] = useState(null);
  const [selectedAssetTypes, setSelectedAssetTypes]: [AssetType[] | null, Function] = useState(null);
  const [userPhone, setUserPhone]: [string | null, Function] = useState(null);

  const menuItemContactCompanyList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'name',
    optionList: contactCompanyList,
    defaultImage: CompanyDefaultImage,
  }), [contactCompanyList]);

  const menuItemRoleList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: roleList,
  }), [roleList]);

  const menuItemAssetTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionList: assetTypeList,
    selectedItemList: selectedAssetTypes,
  }), [assetTypeList, selectedAssetTypes]);

  const onContactClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    contactId: number,
  ): void => {
    navigate({
      pathname: `${ROUTE_CONTACT}/${contactId}`,
    });

    event.stopPropagation();
  }, []);

  const assetTypesChange: Function = useCallback((assetTypeValues: AssetType[]): void => {
    if (onAssetTypesChange) {
      onAssetTypesChange(assetTypeValues);
    }

    setSelectedAssetTypes(assetTypeValues);
  }, []);

  const addressSearchCompleted: Function = useCallback((addressObject: JsonObject): void => {
    if (onAddressSearchCompleted) {
      onAddressSearchCompleted(addressObject);
    }

    setUserAddress(addressObject);
  }, []);

  const addressFieldKeyDown: Function = useCallback((): void => {
    if (userAddress) {
      if (onAddressFieldKeyDown) {
        onAddressFieldKeyDown();
      }

      setUserAddress(null);
    }
  }, [userAddress]);

  const phoneChange: PhoneChangeHandlerFunction = useCallback((phoneValue?: string): void => {
    if (phoneValue !== userPhone) {
      setUserPhone(phoneValue ? FormatService.formatPhoneValue(phoneValue) : null);

      if (onPhoneChange) {
        onPhoneChange(phoneValue);
      }
    }
  }, [userPhone]);

  const phoneWorkChange: PhoneChangeHandlerFunction = useCallback((phoneWorkValue?: string): void => {
    if (onPhoneWorkChange) {
      onPhoneWorkChange(phoneWorkValue);
    }
  }, []);

  const verifyPhone: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    if (onVerifyPhone) {
      onVerifyPhone(userPhone);
    }

    event.stopPropagation();
  }, [userPhone]);

  useEffect((): void => {
    if (userData) {
      if (!userAddress) {
        setUserAddress(userData.address);
      }

      if (!userPhone) {
        setUserPhone(userData.phone);
      }

      if (!selectedAssetTypes) {
        setSelectedAssetTypes(userData.assetTypes || []);
      }

      if (!selectedDeals) {
        setSelectedDeals(userData.accountUserDeals || []);
      }

      if (!selectedPortfolios) {
        setSelectedPortfolios(userData.accountUserPortfolios || []);
      }

      if (!accountUserType) {
        setAccountUserType(userData.accountUserType);
      }
    }
  }, [userData]);

  return (
    <SinglePageTab
      isSaving={isSaving}
      isSaveDisabled={isSaveDisabled}
      isEditMode={isEditMode}
      isRightDrawerOpen={isRightDrawerOpen}
      isEmpty={!userData}
      formError={formError}
      onCancelForm={onCancelForm}
      onSubmitForm={onSubmitForm}
    >
      <Table>
        <TableBody isPlain withNoBorders withTwoColumns withHeaderColumn>
          <TableRow>
            <TableCell>
              First Name
              {isEditMode && <RequiredMark />}
            </TableCell>
            <TableCell>
              {
                isEditMode ? (
                  <FormFieldText isBordered name="firstName" />
                ) : (
                  userData.firstName
                )
              }
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              Last Name
              {isEditMode && <RequiredMark />}
            </TableCell>
            <TableCell>
              {
                isEditMode ? (
                  <FormFieldText isBordered name="lastName" />
                ) : (
                  userData.lastName
                )
              }
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              E-mail
              {isEditMode && <RequiredMark />}
            </TableCell>
            <TableCell>
              {
                isEditMode ? (
                  <FormFieldText isBordered isDisabled={isEditMode && isUserProfile} name="email" />
                ) : (
                  userData.email && (
                    <FilteredLink isEmail link={userData.email}>
                      {userData.email}
                    </FilteredLink>
                  )
                )
              }
            </TableCell>
          </TableRow>
          {
            (userData.phone || isEditMode) && (
              <TableRow>
                <TableCell>
                  Phone
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FieldPhoneBlock>
                        <FormFieldPhone isBordered name="phone" onChange={phoneChange} />
                        <FormFieldNumber isBordered withNoCommas name="phoneExtension" placeholder="Ext" />
                      </FieldPhoneBlock>
                    ) : (
                      userData.phone && userData.phoneText && (
                        <FilteredLink
                          isPhone
                          link={FormatService.formatCleanIntegerNumberString(userData.phone)}
                          phoneExtension={userData.phoneExtension}
                        >
                          {
                            `${userData.phoneText}`
                            + `${userData.phoneExtension ? ` ext. ${userData.phoneExtension}` : ''}`
                          }
                        </FilteredLink>
                      )
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            ((userData.phone && !isEditMode) || (isEditMode && !isUserProfile)) && (
              <TableRow>
                <TableCell>
                  Phone Verified
                  {
                    !userIsPhoneVerified && (
                      <Hint
                        title="In order to verify a phone we send a code to the device with specified phone number"
                      />
                    )
                  }
                </TableCell>
                <TableCell>
                  {
                    isEditMode && !isUserProfile && (
                      <FormFieldCheckbox
                        name="isPhoneVerified"
                        onChange={isUserProfile ? ((event?: React.ChangeEvent<HTMLInputElement>): void => {
                          if (event) {
                            event.preventDefault();
                          }
                        }) : undefined}
                      />
                    )
                  }
                  {
                    !isEditMode && (
                      <ActionBlock
                        isColumnView={isMobileScreen}
                        isSuccessful={userData.isPhoneVerified}
                        withNoButton={userData.isPhoneVerified}
                        labelSuccess="Verified"
                        labelError="Not Verified"
                        labelButton="Verify"
                        onButtonClick={verifyPhone}
                      />
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (userData.phoneWork || isEditMode) && (
              <TableRow>
                <TableCell>
                  Work Phone
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FieldPhoneBlock>
                        <FormFieldPhone isBordered name="phoneWork" onChange={phoneWorkChange} />
                        <FormFieldNumber isBordered withNoCommas name="phoneWorkExtension" placeholder="Ext" />
                      </FieldPhoneBlock>
                    ) : (
                      userData.phoneWork && userData.phoneWorkText && (
                        <FilteredLink
                          isPhone
                          link={FormatService.formatCleanIntegerNumberString(userData.phoneWork)}
                          phoneExtension={userData.phoneWorkExtension}
                        >
                          {
                            `${userData.phoneWorkText}`
                            + `${userData.phoneWorkExtension ? ` ext. ${userData.phoneWorkExtension}` : ''}`
                          }
                        </FilteredLink>
                      )
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            ((userData.company && !isEditMode) || (isEditMode && (!userData.company
              || (userData.company && contactCompanyList && !!contactCompanyList.length)))) && (
              <TableRow>
                <TableCell>
                  Company
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldSelect
                        isBordered
                        withSearch
                        withBlankElement
                        isDisabled={contactCompanyList && !contactCompanyList.length}
                        name="companyId"
                        optionsTotal={contactCompanyList && contactCompanyList.length}
                        onSearch={onContactCompanySearch}
                        onClose={onContactCompanySearchClose}
                      >
                        {menuItemContactCompanyList}
                      </FormFieldSelect>
                    ) : (
                      userData.company && (
                        <ChipLink
                          isDisabled={isEditMode}
                          label={userData.company.name}
                          imageLink={userData.company.photoThumbnail || userData.company.photo
                            || CompanyDefaultImage}
                          permission={CONTACT_VIEW}
                          onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                            onContactClick(event, userData.company.id);
                          }}
                        />
                      )
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (userData.streetAddress || isEditMode) && (
              <TableRow>
                <TableCell>
                  Address
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FieldAddressBlock withOffset>
                        <FormFieldAddress
                          isBordered
                          name="streetAddress"
                          onAddressSearchCompleted={addressSearchCompleted}
                          onKeyDown={addressFieldKeyDown}
                        />
                        <FormFieldText isDisabled isBordered name="location" />
                        <FormFieldText isDisabled isBordered name="postalCode" />
                        <FormFieldText isHidden name="address" />
                      </FieldAddressBlock>
                    ) : (
                      <FilteredLink isExternal link={userData.address && userData.address.url}>
                        {
                          FormatService.formatStringAddress(
                            userData.streetAddress,
                            userData.location,
                            userData.postalCode,
                          )
                        }
                      </FilteredLink>
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (userData.addressDetails || isEditMode) && (
              <TableRow>
                <TableCell>
                  Address Details
                </TableCell>
                <TableCell isTextMultiline>
                  {
                    isEditMode ? (
                      <FormFieldText
                        isBordered
                        isMultiline
                        name="addressDetails"
                        maxRows={FormService.TEXTAREA_MAX_ROWS_SMALL}
                        minRows={FormService.TEXTAREA_MIN_ROWS_DEFAULT}
                      />
                    ) : userData.addressDetails
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (userData.roleText || isEditMode) && (
              <TableRow>
                <TableCell>
                  Role
                  {isEditMode && <RequiredMark />}
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldSelect
                        isBordered
                        isDisabled={roleList && !roleList.length}
                        name="role"
                        optionsTotal={roleList && roleList.length}
                      >
                        {menuItemRoleList}
                      </FormFieldSelect>
                    ) : userData.roleText
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (userData.title || isEditMode) && (
              <TableRow>
                <TableCell>
                  Title
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldText isBordered withNoAutocomplete name="title" />
                    ) : userData.title
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (userData.bio || isEditMode) && (
              <TableRow>
                <TableCell>
                  Bio
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldText
                        isBordered
                        isMultiline
                        name="bio"
                        maxRows={FormService.TEXTAREA_MAX_ROWS_MEDIUM}
                        minRows={FormService.TEXTAREA_MIN_ROWS_DEFAULT}
                      />
                    ) : userData.bio
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            ((userData.assetTypes && !!userData.assetTypes.length) || isEditMode) && (
              <TableRow>
                <TableCell>
                  Asset Focus
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldMultiSelect
                        isBordered
                        isDisabled={assetTypeList && !assetTypeList.length}
                        name="assetTypes"
                        optionList={assetTypeList}
                        onValuesChange={assetTypesChange}
                      >
                        {menuItemAssetTypeList}
                      </FormFieldMultiSelect>
                    ) : (
                      userData.assetTypes.map((assetTypeItem: AssetType): ReactElement => (
                        <Chip
                          isDisabled
                          key={assetTypeItem.id}
                          label={assetTypeItem.title}
                        />
                      ))
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
        </TableBody>
      </Table>
    </SinglePageTab>
  );
});

export default SettingsUserSummary;
